import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import * as React from 'react';
import { useEventCallback } from '@cian/react-utils';
import { ownerDocument, clamp } from '../../../../../internal/utils';
import { doesSupportTouchActionNone, percentToValue, asc, findClosestValueIndex, setValueIndex } from '../../helpers';
import { useFinger } from './useFinger';
export function useMouseTouchController(_ref) {
  var props = _ref.props,
      valueState = _ref.valueState,
      sliderRef = _ref.sliderRef,
      setFocusIndex = _ref.setFocusIndex,
      setActiveIndex = _ref.setActiveIndex,
      setOpenIndex = _ref.setOpenIndex,
      roundValue = _ref.roundValue;
  var onMouseDown = props.onMouseDown,
      disabled = props.disabled,
      min = props.min,
      max = props.max;
  var valueDerived = valueState.valueDerived,
      values = valueState.values,
      changeValue = valueState.changeValue;

  var _useFinger = useFinger(),
      _useFinger2 = _slicedToArray(_useFinger, 2),
      getFinger = _useFinger2[0],
      setFinger = _useFinger2[1];

  var previousIndex = React.useRef(null);

  var getFingerNewValue = function getFingerNewValue(_ref2) {
    var finger = _ref2.finger,
        values2 = _ref2.values,
        source = _ref2.source,
        _ref2$move = _ref2.move,
        move = _ref2$move === void 0 ? false : _ref2$move;
    var slider = sliderRef.current;

    var _getBoundingClientRec = slider.getBoundingClientRect(),
        width = _getBoundingClientRec.width,
        left = _getBoundingClientRec.left;

    var percent = (finger.x - left) / width;
    var newValue;
    newValue = percentToValue(percent, min, max);
    newValue = roundValue(newValue);
    newValue = clamp(newValue, min, max);
    var activeIndex = previousIndex.current || 0;

    if (!move) {
      activeIndex = findClosestValueIndex(values2, newValue);
    }

    if (Array.isArray(source)) {
      var previousValue = newValue;
      newValue = setValueIndex({
        values: values2,
        source: source,
        newValue: newValue,
        index: activeIndex
      }).sort(asc);
      activeIndex = newValue.indexOf(previousValue);
      previousIndex.current = activeIndex;
    }

    return {
      newValue: newValue,
      activeIndex: activeIndex
    };
  };

  var handleTouchMove = useEventCallback(function (nativeEvent) {
    var finger = getFinger(nativeEvent);

    if (!finger) {
      return;
    } // Cancel move in case some other element consumed a mouseup event and it was not fired.


    if (nativeEvent.type === 'mousemove' && nativeEvent.buttons === 0) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return handleTouchEnd(nativeEvent);
    }

    var _getFingerNewValue = getFingerNewValue({
      finger: finger,
      move: true,
      values: values,
      source: valueDerived
    }),
        newValue = _getFingerNewValue.newValue,
        activeIndex = _getFingerNewValue.activeIndex;

    setFocusIndex(activeIndex);
    changeValue(newValue);
  });
  var handleTouchEnd = useEventCallback(function (nativeEvent) {
    setActiveIndex(-1);

    if (nativeEvent.type === 'touchend') {
      setOpenIndex(-1);
    }

    setFinger(null); // eslint-disable-next-line @typescript-eslint/no-use-before-define

    stopListening();
  });
  var handleTouchStart = useEventCallback(function (event) {
    // If touch-action: none; is not supported we need to prevent the scroll manually.
    if (!doesSupportTouchActionNone()) {
      event.preventDefault();
    }

    var touch = event.changedTouches[0];

    if (touch) {
      setFinger(event);
    }

    var finger = getFinger(event);

    var _getFingerNewValue2 = getFingerNewValue({
      finger: finger,
      values: values,
      source: valueDerived
    }),
        newValue = _getFingerNewValue2.newValue,
        activeIndex = _getFingerNewValue2.activeIndex;

    setFocusIndex(activeIndex);
    changeValue(newValue);
    var doc = ownerDocument(sliderRef.current);
    doc.addEventListener('touchmove', handleTouchMove);
    doc.addEventListener('touchend', handleTouchEnd);
  });
  var stopListening = React.useCallback(function () {
    var doc = ownerDocument(sliderRef.current);
    doc.removeEventListener('mousemove', handleTouchMove);
    doc.removeEventListener('mouseup', handleTouchEnd);
    doc.removeEventListener('touchmove', handleTouchMove);
    doc.removeEventListener('touchend', handleTouchEnd); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleTouchEnd, handleTouchMove]);
  React.useEffect(function () {
    var slider = sliderRef.current;

    if (!slider) {
      return;
    }

    slider.addEventListener('touchstart', handleTouchStart, {
      passive: doesSupportTouchActionNone()
    });
    return function () {
      slider.removeEventListener('touchstart', handleTouchStart, {
        passive: doesSupportTouchActionNone()
      });
      stopListening();
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopListening, handleTouchStart]);
  React.useEffect(function () {
    if (disabled) {
      stopListening();
    }
  }, [disabled, stopListening]);
  var handleMouseDown = useEventCallback(function (event) {
    var finger = getFinger(event.nativeEvent);

    if (onMouseDown) {
      onMouseDown(event);
    } // Only handle left clicks


    if (event.button !== 0 || !finger) {
      return;
    } // Avoid text selection


    event.preventDefault();

    var _getFingerNewValue3 = getFingerNewValue({
      finger: finger,
      values: values,
      source: valueDerived
    }),
        newValue = _getFingerNewValue3.newValue,
        activeIndex = _getFingerNewValue3.activeIndex;

    setFocusIndex(activeIndex);
    changeValue(newValue);
    var doc = ownerDocument(sliderRef.current);
    doc.addEventListener('mousemove', handleTouchMove);
    doc.addEventListener('mouseup', handleTouchEnd);
  });
  return {
    onMouseDown: handleMouseDown
  };
}